import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Sparkles, ArrowRight, ChevronDown, ChevronUp,
  CheckCircle2, BookOpen, Mail, MapPin, Search,
} from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { buildOutboundUrl, trackCTA } from '@/lib/getYourAnswerApi';

// ─── EXACT SOURCE COPY ──────────────────────────────────────────────────────
// Pulled verbatim from /tmp/schedule_a_meet/KGOB-Ask-a-CPA-offline/Landing Page.html
// (the Editorial voice variant from HERO_COPY, plus the static HTML sections)

const HERO = {
  // Note: <em> wraps "Bring it to a CPA firm." in source
  headline: 'The tax worry you keep Googling?',
  headlineEm: 'Bring it to a CPA firm.',
  subhead:
    'Find the question that matches your exact situation, then talk it through with KGOB — your licensed Charlotte CPA firm — in thirty focused minutes, for a flat fee, with a clear next step.',
  final: 'Stop Googling it. Bring it to KGOB.',
  creditChip: 'One flat fee · no commitment',
};

// EXACT pricing tiers from source's K.PRICING.sessions array
const PRICING_TIERS = [
  {
    name: 'Quick Question',
    min: 30,
    blocks: 1,
    price: 95,
    tagline: 'One specific issue.',
    examples: 'An IRS letter · "Do I owe quarterly taxes?" · A single deduction.',
    feat: true, // "Most popular"
  },
  {
    name: 'Deep Dive',
    min: 60,
    blocks: 2,
    price: 170,
    tagline: 'A structural question.',
    examples: 'LLC vs S-corp · An audit · Crypto reporting · First-year freelancer.',
    feat: false,
  },
  {
    name: 'Strategy Session',
    min: 90,
    blocks: 3,
    price: 240,
    tagline: 'Multiple topics or planning.',
    examples: 'Year-end planning · Selling a business · Multi-state move.',
    feat: false,
  },
];

const PRICING_PARAS = [
  "Sessions come in 30-minute blocks, so the entry price stays low and you're never boxed in. Most questions fit in one block. Bigger, structural questions can take two or three.",
  "No commitment, no upsell. You get a clear answer and a recommended next step — that's it. Keep working with KGOB only if you decide it's worth it.",
  "Your time is confirmed once payment goes through. Free reschedule up to 24 hours before; sessions are non-refundable and remain valid for 180 days. North Carolina professional services aren't subject to sales tax, so there's no tax on your session.",
];

const HOW_STEPS = [
  { title: 'Find your question', desc: 'Search or browse to the situation that matches yours.' },
  { title: 'Pick a time',        desc: 'Choose a slot that works. Length is pre-set to fit the question.' },
  { title: 'Quick intake',       desc: 'A few short questions so your CPA arrives prepared.' },
  { title: 'Pay to confirm',     desc: 'Your slot is held the moment payment goes through.' },
  { title: 'Get the invite',     desc: 'A calendar invite with your meeting link lands in your inbox.' },
];

// EXACT 8 FAQs from source script #4 (faqs array)
const FAQ_ITEMS = [
  {
    q: 'Who actually handles my session?',
    a: 'KGOB — Kohari Gonzalez Oneyear & Brown, a licensed CPA firm in Charlotte led by Sara Gonzalez, CPA. We match your question to the right person on our team for your situation: that\u2019s often a CPA, and always a qualified professional working under the firm. It\u2019s never a chatbot, a call center, or generic advice.',
  },
  {
    q: 'What if I need more than 30 minutes?',
    a: 'Pick a longer session up front — Deep Dive (60 min, $170) or Strategy (90 min, $240) — or add a block. Your CPA will tell you if your question needs more time.',
  },
  {
    q: 'Do I have to keep working with you afterward?',
    a: 'No. It\u2019s a one-time, flat-fee session — a clear answer and a recommended next step. If you\u2019d like ongoing help we\u2019ll show you what that looks like; if not, there\u2019s no pressure and no upsell.',
  },
  {
    q: 'Can I reschedule or cancel?',
    a: 'Yes — reschedule for free up to 24 hours before your session. Sessions are non-refundable, and your paid session stays valid for 180 days.',
  },
  {
    q: 'Is the session recorded?',
    a: 'Only if you want it to be. You can opt in at booking to get a recording for your own notes — otherwise it isn\u2019t recorded.',
  },
  {
    q: 'Is my information private?',
    a: 'Yes. Any documents you share at intake route securely to our Document Vault — never email. We treat your tax information with the confidentiality you\u2019d expect from a CPA firm.',
  },
  {
    q: 'Do you handle my situation?',
    a: 'Almost certainly. The library spans 23 topics and thousands of variations across personas and states. If you don\u2019t see an exact match, book a session and describe it in your own words.',
  },
  {
    q: 'Do you charge sales tax?',
    a: 'No. North Carolina doesn\u2019t tax CPA professional services, so there\u2019s no sales tax added to your session fee.',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// Section eyebrow — hoisted outside the page component so React doesn't
// recreate it on every render (was previously a nested-component lint error).
function Eyebrow({ children }) {
  return (
    <div className="flex items-center justify-center gap-3 mb-3">
      <span className="h-px w-8 bg-[#D4AF37]" />
      <span className="font-['Inter'] text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">{children}</span>
      <span className="h-px w-8 bg-[#D4AF37]" />
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function GetYourAnswer() {
  const [openFaq, setOpenFaq] = useState(null);
  const [topics, setTopics] = useState([]);
  const [canonicalTotal, setCanonicalTotal] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Topics (static metadata for the finder cards)
    fetch('/data/get-your-answer/topics.json')
      .then((r) => r.json())
      .then(setTopics)
      .catch(() => setTopics([]));

    // Live canonical count from the API — auto-updates as the library grows
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/get-your-answer/topics`)
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d.canonical_total === 'number') setCanonicalTotal(d.canonical_total);
      })
      .catch(() => { /* keep null; copy will render a sensible fallback */ });
  }, []);

  // Search hands off to /library?q=... for short keyword queries, or
  // /ask?q=... when the user typed a real question (contains '?' or is long
  // enough to look like a sentence). Keeps short keyword-style queries on the
  // library page (where filter chips are useful) and routes natural-language
  // questions straight to the booking page.
  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchValue.trim();
    if (!q) return;
    trackCTA('hero-search', {});
    const looksLikeQuestion = q.includes('?') || q.split(/\s+/).length >= 6;
    if (looksLikeQuestion) {
      navigate(`/get-your-answer/ask?q=${encodeURIComponent(q)}`);
    } else {
      navigate(`/get-your-answer/library?q=${encodeURIComponent(q)}`);
    }
  };

  const goToAppKgob = (source, ctx = {}) => {
    trackCTA(source, ctx);
    window.location.href = buildOutboundUrl(source, ctx);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="get-your-answer-page">
      <Helmet>
        <title>CPA Q&A | Expert Tax & Accounting Answers | KGOB</title>
        <meta
          name="description"
          content="Bring your exact tax situation to KGOB, a licensed Charlotte CPA firm. A focused 30-minute session for a flat $95 — one flat fee, no commitment."
        />
        <link rel="canonical" href="https://kgob.com/get-your-answer" />
        <meta property="og:title" content="CPA Q&A | Expert Tax & Accounting Answers | KGOB" />
        <meta property="og:description" content="Bring your exact tax situation to KGOB, a licensed Charlotte CPA firm. A focused 30-minute session with a CPA." />
        <meta property="og:url" content="https://kgob.com/get-your-answer" />
      </Helmet>

      <Header />

      <main>
        {/* ── HERO (Editorial voice, source-verbatim) ── */}
        <section className="relative bg-[linear-gradient(180deg,#0B2240_0%,#07182F_100%)] text-white overflow-hidden" data-testid="gya-hero">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 18% 22%, #D4AF37 0%, transparent 38%), radial-gradient(circle at 82% 78%, #2FA3B5 0%, transparent 42%)',
            }}
          />
          <div className="relative max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 lg:py-32 text-center">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center gap-2 mb-6"
            >
              <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
              <span className="font-['Inter'] text-[11px] tracking-[0.18em] uppercase text-[#D4AF37] font-semibold">
                {HERO.creditChip}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.08 }}
              className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-[-0.01em] font-semibold mb-6"
              data-testid="gya-hero-headline"
            >
              {HERO.headline} <em className="text-[#D4AF37] not-italic font-medium">{HERO.headlineEm}</em>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.18 }}
              className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed mb-8"
              data-testid="gya-hero-subhead"
            >
              {HERO.subhead}
            </motion.p>

            {/* Search box for the library — primary entry into the flow */}
            <motion.form
              onSubmit={onSearchSubmit}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.28 }}
              className="max-w-xl mx-auto"
              data-testid="gya-hero-search-form"
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4C5A70]/70" />
                <Input
                  type="text"
                  placeholder='Find your question — e.g. "CP2000", "unfiled returns", "LLC vs S-corp"'
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="pl-12 pr-32 h-14 bg-white text-[#0B2240] border-[#D4AF37]/40 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] text-base placeholder:text-[#4C5A70]/50"
                  data-testid="gya-hero-search-input"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-10 px-5 rounded-md bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold text-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-110"
                  data-testid="gya-hero-search-submit"
                >
                  Search
                </button>
              </div>
              <p className="mt-3 text-[12px] text-white/55">
                Or{' '}
                <Link data-testid="get-your-answer-link-1" to="/get-your-answer/library" onClick={() => trackCTA('hero-browse', {})} className="underline underline-offset-4 hover:text-white">
                  browse the full question library
                </Link>{' '}
                {canonicalTotal
                  ? `— ${canonicalTotal.toLocaleString()} questions across 23 situations, personalizable to your state.`
                  : `— thousands of questions across 23 situations, personalizable to your state.`}
              </p>
              {process.env.REACT_APP_GYA_CUSTOMER_LOGIN_ENABLED === 'true' && (
                <p className="mt-2 text-[12px] text-white/45">
                  Already booked a session?{' '}
                  <a
                    href="https://app.kgob.com/ask-a-question/portal/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackCTA('hero-customer-login', {})}
                    className="underline underline-offset-4 hover:text-[#D4AF37]"
                    data-testid="gya-hero-customer-login"
                  >
                    Sign in to manage it →
                  </a>
                </p>
              )}
            </motion.form>

            <p className="mt-12 italic text-white/55 text-sm" data-testid="gya-hero-final-line">{HERO.final}</p>
          </div>
        </section>

        {/* ── FINDER section header (source: "Find your situation" — but we lead with search above; here we show the library entry) ── */}
        <section id="finder" className="bg-white py-20 sm:py-24" data-testid="gya-finder-section">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
            <Eyebrow>Find your situation</Eyebrow>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0B2240] leading-tight mb-3" data-testid="gya-finder-h2">
              Find your situation
            </h2>
            <p className="text-[#4C5A70] max-w-2xl mx-auto mb-10">
              Real questions, tagged to real situations. Tap the one that sounds like you.
            </p>

            <Link
              to="/get-your-answer/library"
              onClick={() => trackCTA('finder-library-link', {})}
              className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-[#0B2240] text-white text-sm font-semibold hover:bg-[#0B2240]/90 transition-colors"
              data-testid="gya-finder-browse-library"
            >
              <BookOpen className="w-4 h-4" />
              Browse the full question library
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* ── PRICING (source-verbatim 3 tiers + 3 paragraphs) ── */}
        <section id="pricing" className="bg-[#F4EFE3] py-20 sm:py-24" data-testid="gya-pricing-section">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-10">
              <Eyebrow>Simple, flat pricing</Eyebrow>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0B2240] leading-tight mb-3">
                Pick how much time your question needs.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-testid="gya-pricing-grid">
              {PRICING_TIERS.map((tier) => (
                <div
                  key={tier.name}
                  className={`relative p-6 rounded-2xl bg-white ${
                    tier.feat ? 'border-2 border-[#D4AF37] shadow-lg' : 'border border-[#E1DFD7]'
                  }`}
                  data-testid={`gya-pricing-tier-${tier.name.toLowerCase().replace(' ', '-')}`}
                >
                  {tier.feat && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#D4AF37] text-[#0B2240] text-[10px] tracking-[0.18em] uppercase font-bold">
                      Most popular
                    </span>
                  )}
                  <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-1">{tier.name}</h3>
                  <div className="text-[10px] tracking-[0.16em] uppercase text-[#D4AF37] font-semibold mb-3">
                    {tier.min} MIN · {tier.blocks} {tier.blocks > 1 ? 'BLOCKS' : 'BLOCK'}
                  </div>
                  <div className="font-['Playfair_Display'] text-4xl text-[#0B2240] font-semibold mb-1">
                    ${tier.price}<span className="text-sm text-[#4C5A70] font-normal ml-1">flat</span>
                  </div>
                  <p className="text-sm text-[#0B2240] font-medium mt-2 mb-2">{tier.tagline}</p>
                  <p className="text-xs text-[#4C5A70] leading-relaxed mb-5">{tier.examples}</p>
                  <Button
                    onClick={() => {
                      const slug = tier.name.toLowerCase().replace(' ', '-');
                      const stMap = {
                        'quick-question': 'quick',
                        'deep-dive': 'deep_dive',
                        'strategy-session': 'strategy',
                      };
                      goToAppKgob('pricing-' + slug, {
                        session_type: stMap[slug],
                        campaign: 'pricing_' + slug,
                      });
                    }}
                    className={`w-full h-11 rounded-xl text-sm font-semibold ${
                      tier.feat
                        ? 'bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-110'
                        : 'bg-white border border-[#0B2240]/20 text-[#0B2240] hover:bg-[#0B2240] hover:text-white transition-colors'
                    }`}
                    data-testid={`gya-pricing-cta-${tier.name.toLowerCase().replace(' ', '-')}`}
                  >
                    Book a {tier.name.toLowerCase()}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Source-verbatim trailing paragraphs */}
            <div className="max-w-3xl mx-auto text-center space-y-3 text-[#4C5A70] leading-relaxed text-sm">
              {PRICING_PARAS.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW (source-verbatim 5 steps) ── */}
        <section id="how" className="bg-white py-20 sm:py-24" data-testid="gya-how-section">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-12">
              <Eyebrow>How it works</Eyebrow>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0B2240] leading-tight">
                From &ldquo;what do I do?&rdquo; to a calendar invite in minutes.
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {HOW_STEPS.map((step, i) => (
                <div key={step.title} className="bg-white border border-[#E1DFD7] rounded-xl p-5 hover:border-[#D4AF37]/60 transition-colors">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-[#D4AF37] font-semibold mb-2">Step {i + 1}</div>
                  <h3 className="font-['Playfair_Display'] text-lg text-[#0B2240] mb-2">{step.title}</h3>
                  <p className="text-sm text-[#4C5A70] leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SITUATIONS (source: "Who it's for" / 23 topics) ── */}
        <section id="situations" className="bg-[linear-gradient(180deg,#F7F6F2_0%,#F4EFE3_100%)] py-20 sm:py-24" data-testid="gya-situations-section">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-12">
              <Eyebrow>Who it&rsquo;s for</Eyebrow>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0B2240] leading-tight mb-3">
                If one of these is your week, this was built for you.
              </h2>
              <p className="text-[#4C5A70] max-w-2xl mx-auto">
                The library spans 23 situations and thousands of variations across personas and all 50 states &mdash; so you can almost always find your own.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gya-situations-grid">
              {topics.map((t) => (
                <Link
                  key={t.id}
                  to="/get-your-answer/library"
                  state={{ topic: t.id }}
                  onClick={() => trackCTA('topic-pill', { topic: t.id })}
                  className={`group block p-4 rounded-xl border bg-white hover:shadow-md transition-all ${
                    t.urgency === 'high' ? 'border-[#D4AF37]/40 hover:border-[#D4AF37]' : 'border-[#E1DFD7] hover:border-[#0B2240]/30'
                  }`}
                  data-testid={`gya-topic-pill-${t.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-[#0B2240] text-sm leading-snug">{t.label}</h3>
                    <ArrowRight className="w-3 h-3 text-[#4C5A70]/40 group-hover:translate-x-1 group-hover:text-[#0B2240] transition-all flex-shrink-0 mt-1" />
                  </div>
                  <p className="text-[11px] text-[#4C5A70]/70 mt-1">{t.questionCount.toLocaleString()} questions</p>
                </Link>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/get-your-answer/library"
                onClick={() => trackCTA('situations-library-link', {})}
                className="inline-flex items-center gap-2 text-sm text-[#0B2240] hover:text-[#D4AF37] font-semibold underline-offset-4 hover:underline"
                data-testid="gya-situations-browse-all"
              >
                <BookOpen className="w-4 h-4" /> Browse the full question library
              </Link>
            </div>
          </div>
        </section>

        {/* ── FAQ (source-verbatim 8 items) ── */}
        <section id="faq" className="bg-[#F7F6F2] py-20 sm:py-24" data-testid="gya-faq-section">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-10">
              <Eyebrow>Questions about the session</Eyebrow>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0B2240] leading-tight">
                Good questions. Here&rsquo;s the straight version.
              </h2>
            </div>

            <div className="space-y-2">
              {FAQ_ITEMS.map((item, i) => (
                <div key={i} className="bg-white border border-[#E1DFD7] rounded-xl overflow-hidden" data-testid={`gya-faq-item-${i}`}>
                  <button data-testid="get-your-answer-button-1"
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-[#F4EFE3]/40 transition-colors"
                    aria-expanded={openFaq === i}
                  >
                    <span className="font-['Playfair_Display'] text-lg text-[#0B2240]">{item.q}</span>
                    {openFaq === i ? (
                      <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#4C5A70] flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 text-[#4C5A70] leading-relaxed text-sm">{item.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA (source-verbatim) ── */}
        <section className="bg-[linear-gradient(180deg,#0B2240_0%,#07182F_100%)] text-white py-20 sm:py-24" data-testid="gya-final-cta-section">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
            <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl leading-tight tracking-[-0.01em] mb-6">
              Stop Googling it. Ask a CPA.
            </h2>
            <p className="text-white/80 leading-relaxed mb-8 text-base sm:text-lg max-w-2xl mx-auto">
              Find your situation, book thirty minutes, and walk away with a clear answer &mdash; ninety-five dollars, no commitment.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/get-your-answer/library"
                onClick={() => trackCTA('final-find-question', {})}
                className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold text-base shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-110"
                data-testid="gya-final-cta-button"
              >
                <Sparkles className="w-4 h-4" /> Find your question
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-12 flex flex-col items-center gap-3 text-xs text-white/55 tracking-[0.08em] uppercase">
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> One flat fee, no commitment</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> Free reschedule up to 24h before</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> A licensed CPA firm</span>
              </div>
              <div className="mt-6 text-[10px] text-white/40 tracking-[0.14em] normal-case">
                <div className="flex items-center justify-center gap-2 mb-1"><MapPin className="w-3 h-3" />
                  In-person at Charlotte HQ (2740 East WT Harris Blvd, Suite 200, Charlotte, NC 28213) &middot; Winston-Salem (326 South Main Street, Winston-Salem, NC 27101)
                </div>
                <div className="flex items-center justify-center gap-2"><Mail className="w-3 h-3" /> 1-844-599-3355 &middot; connect@kgob.com</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
